import React, { useEffect, useRef, useState } from "react";
import { fmtAbbr } from "../../components/terminal/format.js";

/* Lit-globe pane — country nodes sized by 24h gross, arcs by
   corridor + rail color + animated particle. Below the globe a
   sortable in-flight corridor list fills the remaining space.

   Data props match BACKEND_TERMINAL_SPEC.md A.1 + A.2 verbatim. The
   land-mass polygons are stylized; not geographically perfect, but
   visually clean against the dark canvas. */

const W = 1100, H = 540;
const proj = (lat, lon) => ({
  x: ((lon + 180) / 360) * W,
  y: ((90 - lat) / 180) * H,
});

const RAIL_COLORS = {
  SWIFT:  "#6366F1", SEPA: "#06B6D4", "SEPA-I": "#06B6D4",
  ACH:    "#F5A524", FedNow: "#EC4899",
  USDC:   "#2CCB7E", USDT: "#2CCB7E",
  FX:     "#A78BFA",
};

const LAND_PATHS = [
  "M 175 130 L 240 105 L 310 105 L 360 145 L 360 210 L 320 235 L 280 240 L 250 270 L 220 270 L 195 240 L 175 200 Z",
  "M 230 270 L 270 280 L 275 305 L 250 305 Z",
  "M 290 320 L 330 305 L 350 340 L 345 405 L 320 440 L 295 430 L 285 380 Z",
  "M 380 90 L 420 80 L 430 110 L 410 125 L 385 120 Z",
  "M 510 130 L 555 115 L 605 130 L 615 165 L 580 180 L 555 175 L 525 165 Z",
  "M 540 200 L 605 195 L 630 240 L 640 305 L 605 365 L 570 360 L 545 305 L 540 250 Z",
  "M 615 195 L 660 200 L 670 240 L 645 245 L 625 230 Z",
  "M 615 100 L 800 90 L 870 110 L 880 140 L 830 165 L 730 165 L 660 155 L 625 150 Z",
  "M 720 200 L 760 200 L 770 245 L 750 280 L 720 270 L 705 235 Z",
  "M 800 270 L 870 280 L 880 305 L 845 315 L 815 305 Z",
  "M 780 165 L 855 170 L 870 215 L 840 245 L 800 240 L 780 210 Z",
  "M 905 175 L 925 170 L 935 195 L 920 215 L 905 205 Z",
  "M 870 360 L 935 355 L 950 395 L 920 420 L 880 415 Z",
  "M 500 130 L 515 125 L 520 145 L 505 150 Z",
];

export default function GlobePane({ countries = [], countryVolume = {}, corridors = [] }) {
  const [hover, setHover] = useState(null);
  const [tick, setTick] = useState(0);
  /* Hover lifecycle — popup stays while the user is over a country
     node, a corridor row, or the popup itself; clears 3s after the
     last mouseleave. enterHover cancels any pending dismiss so
     traversing between elements doesn't flash the popup off. */
  const hoverTimerRef = useRef(null);
  const enterHover = (iso) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHover(iso);
  };
  const leaveHover = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setHover(null);
      hoverTimerRef.current = null;
    }, 3000);
  };
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 1000), 50);
    return () => clearInterval(id);
  }, []);

  const totalsAll = Object.values(countryVolume).reduce(
    (a, v) => a + (parseFloat(v.gross) || 0),
    0,
  );
  const corridorTotal = corridors.reduce((a, c) => a + (parseFloat(c.volume_usd) || 0), 0);
  const maxCountry = Math.max(
    1,
    ...Object.values(countryVolume).map((v) => parseFloat(v.gross) || 0),
  );
  const maxCorridor = Math.max(1, ...corridors.map((c) => parseFloat(c.volume_usd) || 0));

  const hoverData = hover
    ? {
        country: countries.find((c) => c.iso === hover),
        vol: countryVolume[hover] || { in: "0", out: "0", gross: "0" },
        corridors: corridors.filter((c) => c.from_iso === hover || c.to_iso === hover),
      }
    : null;

  return (
    <div className="term-pane" style={{ display:"flex", flexDirection:"column" }}>
      <div className="term-pane-head">
        Global flow · 24h
        <span className="meta">{Object.keys(countryVolume).length} countries · {fmtAbbr(corridorTotal)} in motion</span>
      </div>
      <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:0 }}>
        <div style={{ position:"relative", flex:"none", aspectRatio:"1100 / 540", width:"100%", overflow:"hidden" }}>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ position:"absolute", inset:0, width:"100%", height:"100%", display:"block" }}>
            <defs>
              <radialGradient id="t-node-glow">
                <stop offset="0%" stopColor="#2CCB7E" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#2CCB7E" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="t-node-glow-amber">
                <stop offset="0%" stopColor="#F5A524" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#F5A524" stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* graticule */}
            <g stroke="#1A2030" strokeWidth="0.5" fill="none" opacity="0.6">
              {[-60,-30,0,30,60].map((lat) => (
                <line key={"lat"+lat} x1="0" y1={proj(lat, 0).y} x2={W} y2={proj(lat, 0).y} strokeDasharray="2,4" />
              ))}
              {[-150,-120,-90,-60,-30,0,30,60,90,120,150].map((lon) => (
                <line key={"lon"+lon} x1={proj(0, lon).x} y1="0" x2={proj(0, lon).x} y2={H} strokeDasharray="2,4" />
              ))}
              <line x1="0" y1={proj(0, 0).y} x2={W} y2={proj(0, 0).y} stroke="#1A2030" strokeWidth="0.7" opacity="0.9" />
            </g>

            {/* land masses */}
            <g fill="#0E131C" stroke="#1A2030" strokeWidth="0.7">
              {LAND_PATHS.map((d, i) => <path key={i} d={d} />)}
            </g>

            {/* corridor arcs + particle */}
            <g>
              {corridors.map((c, i) => {
                const a = countries.find((x) => x.iso === c.from_iso);
                const b = countries.find((x) => x.iso === c.to_iso);
                if (!a || !b) return null;
                const pa = proj(a.lat, a.lon);
                const pb = proj(b.lat, b.lon);
                const dist = Math.hypot(pb.x - pa.x, pb.y - pa.y) || 1;
                const dx = pb.x - pa.x, dy = pb.y - pa.y;
                const nx = -dy / dist, ny = dx / dist;
                const curve = Math.min(dist * 0.28, 90);
                const cx = (pa.x + pb.x) / 2 + nx * curve;
                const cy = (pa.y + pb.y) / 2 + ny * curve;
                const path = `M ${pa.x} ${pa.y} Q ${cx} ${cy} ${pb.x} ${pb.y}`;
                const color = RAIL_COLORS[c.rail] || "#8E97A8";
                const v = parseFloat(c.volume_usd) || 0;
                const strokeW = 0.6 + (v / maxCorridor) * 2.4;
                const isHover = hover && (c.from_iso === hover || c.to_iso === hover);
                const opacity = hover ? (isHover ? 1 : 0.12) : 0.7;

                const phase = ((tick + i * 13) % 60) / 60;
                const t = phase;
                const px = (1 - t) * (1 - t) * pa.x + 2 * (1 - t) * t * cx + t * t * pb.x;
                const py = (1 - t) * (1 - t) * pa.y + 2 * (1 - t) * t * cy + t * t * pb.y;

                return (
                  <g key={i} style={{ opacity, transition: "opacity 0.15s" }}>
                    <path d={path} stroke={color} strokeWidth={strokeW} fill="none" opacity="0.55" />
                    <circle cx={px} cy={py} r={2.2} fill={color} opacity="0.95">
                      <animate attributeName="r" values="1.8;3;1.8" dur="1.2s" repeatCount="indefinite" />
                    </circle>
                  </g>
                );
              })}
            </g>

            {/* country nodes */}
            <g>
              {countries.map((c) => {
                const p = proj(c.lat, c.lon);
                const v = countryVolume[c.iso];
                if (!v) {
                  return <circle key={c.iso} cx={p.x} cy={p.y} r="2" fill="#2E3645" opacity="0.4" />;
                }
                const gross = parseFloat(v.gross) || 0;
                const inn = parseFloat(v.in) || 0;
                const out = parseFloat(v.out) || 0;
                const r = 3 + (gross / maxCountry) * 8;
                const net = inn - out;
                const isReceiver = net > 0;
                const color = Math.abs(net) < gross * 0.1 ? "#8E97A8" : (isReceiver ? "#2CCB7E" : "#F5A524");
                const isHover = hover === c.iso;
                const otherHover = hover && hover !== c.iso;
                return (
                  <g
                    key={c.iso}
                    style={{ cursor: "pointer", opacity: otherHover ? 0.4 : 1, transition: "opacity 0.15s" }}
                    onMouseEnter={() => enterHover(c.iso)}
                    onMouseLeave={leaveHover}
                  >
                    <circle cx={p.x} cy={p.y} r={r * 3} fill={`url(#t-node-glow${color === "#F5A524" ? "-amber" : ""})`} />
                    <circle cx={p.x} cy={p.y} r={r} fill="none" stroke={color} strokeWidth="0.8" opacity="0.6">
                      <animate attributeName="r" values={`${r};${r*2};${r}`} dur="2.4s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.6;0;0.6" dur="2.4s" repeatCount="indefinite" />
                    </circle>
                    <circle cx={p.x} cy={p.y} r={r} fill={color} />
                    <circle cx={p.x} cy={p.y} r={r * 0.5} fill="#fff" opacity="0.7" />
                    <text x={p.x + r + 6} y={p.y + 3.5} fill={isHover ? "#E8ECF2" : "#8E97A8"} fontSize="10" fontFamily="JetBrains Mono" style={{ letterSpacing: "0.04em", pointerEvents: "none" }}>
                      {c.iso}
                    </text>
                    {isHover && (
                      <text x={p.x + r + 6} y={p.y + 16} fill={color} fontSize="9" fontFamily="JetBrains Mono" style={{ pointerEvents:"none" }}>
                        {fmtAbbr(gross)}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>

          {/* corner stats */}
          <div style={{ position:"absolute", top:12, left:14, fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.10em", textTransform:"uppercase", fontWeight:600, pointerEvents:"none" }}>
            24H VOLUME
            <b className="mono" style={{ display:"block", fontSize:18, color:"var(--term-ink-1)", fontWeight:500, letterSpacing:"-0.015em", textTransform:"none", marginTop:2 }}>{fmtAbbr(totalsAll)}</b>
          </div>
          <div style={{ position:"absolute", top:12, right:14, textAlign:"right", fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.10em", textTransform:"uppercase", fontWeight:600, pointerEvents:"none" }}>
            CORRIDORS · ACTIVE
            <b className="mono" style={{ display:"block", fontSize:18, color:"var(--term-ink-1)", fontWeight:500, letterSpacing:"-0.015em", textTransform:"none", marginTop:2 }}>{corridors.length}</b>
          </div>

          {hoverData && (
            <div
              onMouseEnter={() => enterHover(hover)}
              onMouseLeave={leaveHover}
              style={{ position:"absolute", bottom:14, left:"50%", transform:"translateX(-50%)", background:"var(--term-bg-pane)", border:"1px solid var(--term-line)", padding:"10px 14px", minWidth:280, fontFamily:"JetBrains Mono", fontSize:11, color:"var(--term-ink-1)", boxShadow:"0 12px 32px rgba(0,0,0,0.5)" }}>
              <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", borderBottom:"1px solid var(--term-line)", paddingBottom:6, marginBottom:8 }}>
                <span style={{ fontFamily:"Inter", fontSize:13, fontWeight:500, letterSpacing:"-0.01em" }}>{hoverData.country?.label || hover}</span>
                <span style={{ color:"var(--term-ink-3)", fontSize:10, letterSpacing:"0.10em" }}>{hover}</span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:8 }}>
                <div><div style={{ fontSize:9, letterSpacing:"0.14em", color:"var(--term-ink-3)", textTransform:"uppercase" }}>IN</div><div style={{ color:"var(--term-green)", fontSize:13, marginTop:2 }}>{fmtAbbr(hoverData.vol.in)}</div></div>
                <div><div style={{ fontSize:9, letterSpacing:"0.14em", color:"var(--term-ink-3)", textTransform:"uppercase" }}>OUT</div><div style={{ color:"var(--term-red)", fontSize:13, marginTop:2 }}>{fmtAbbr(hoverData.vol.out)}</div></div>
                <div><div style={{ fontSize:9, letterSpacing:"0.14em", color:"var(--term-ink-3)", textTransform:"uppercase" }}>NET</div><div style={{ color: (parseFloat(hoverData.vol.in) - parseFloat(hoverData.vol.out)) > 0 ? "var(--term-green)" : "var(--term-amber)", fontSize:13, marginTop:2 }}>{fmtAbbr(parseFloat(hoverData.vol.in) - parseFloat(hoverData.vol.out))}</div></div>
              </div>
              {hoverData.corridors.length > 0 && (
                <div style={{ borderTop:"1px solid var(--term-line-dim)", paddingTop:6 }}>
                  <div style={{ fontSize:9, letterSpacing:"0.14em", color:"var(--term-ink-3)", textTransform:"uppercase", marginBottom:4 }}>CORRIDORS</div>
                  {hoverData.corridors.map((c, i) => {
                    const dir = c.from_iso === hover ? "→" : "←";
                    const other = c.from_iso === hover ? c.to_iso : c.from_iso;
                    return (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:8, fontSize:10.5, padding:"2px 0" }}>
                        <span style={{ color: RAIL_COLORS[c.rail], fontSize:9, padding:"1px 5px", border:`1px solid ${RAIL_COLORS[c.rail] || "#8E97A8"}`, letterSpacing:"0.06em" }}>{c.rail}</span>
                        <span style={{ color:"var(--term-ink-2)" }}>{dir} {other}</span>
                        <span style={{ marginLeft:"auto", color:"var(--term-ink-1)" }}>{fmtAbbr(c.volume_usd)}</span>
                        <span style={{ color:"var(--term-ink-3)" }}>{c.count}×</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* in-flight corridor list */}
        <div style={{ flex:1, minHeight:0, borderTop:"2px solid var(--term-line)", overflow:"auto", background:"var(--term-bg-pane)" }}>
          <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", padding:"10px 14px 8px", borderBottom:"1px solid var(--term-line)", background:"var(--term-bg-pane-2)" }}>
            <div style={{ display:"flex", alignItems:"baseline", gap:10 }}>
              <span style={{ fontFamily:"Inter", fontSize:13, fontWeight:500, color:"var(--term-ink-1)", letterSpacing:"-0.01em" }}>In-flight corridors</span>
              <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)", letterSpacing:"0.04em" }}>{corridors.length} active · {fmtAbbr(corridorTotal)} in motion</span>
            </div>
            <span style={{ fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.10em", textTransform:"uppercase", fontWeight:600 }}>sorted by volume</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"88px 1fr 84px 60px 1fr", padding:"7px 14px", borderBottom:"1px solid var(--term-line)", fontSize:9, letterSpacing:"0.14em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, gap:12, background:"var(--term-bg-pane)" }}>
            <span>Corridor</span><span>Rail</span><span style={{ textAlign:"right" }}>Volume</span><span style={{ textAlign:"right" }}>Pmts</span><span>Share</span>
          </div>
          {[...corridors].sort((a, b) => (parseFloat(b.volume_usd) || 0) - (parseFloat(a.volume_usd) || 0)).map((c, i) => {
            const v = parseFloat(c.volume_usd) || 0;
            const share = corridorTotal ? v / corridorTotal : 0;
            const color = RAIL_COLORS[c.rail] || "#8E97A8";
            const isHover = hover && (c.from_iso === hover || c.to_iso === hover);
            return (
              <div
                key={i}
                onMouseEnter={() => enterHover(c.from_iso)}
                onMouseLeave={leaveHover}
                style={{
                  display:"grid", gridTemplateColumns:"88px 1fr 84px 60px 1fr",
                  padding:"7px 14px", gap:12, alignItems:"center",
                  fontSize:11, fontFamily:"JetBrains Mono",
                  borderBottom:"1px solid var(--term-line-dim)",
                  background: isHover ? "var(--term-hover-bg)" : "transparent",
                  color:"var(--term-ink-1)",
                }}
              >
                <span style={{ color:"var(--term-ink-2)", display:"inline-flex", alignItems:"center", gap:6 }}>
                  <span style={{ color:"var(--term-ink-1)", fontWeight:500 }}>{c.from_iso}</span>
                  <span style={{ color, fontSize:13, lineHeight:1, fontWeight:600 }}>→</span>
                  <span style={{ color:"var(--term-ink-1)", fontWeight:500 }}>{c.to_iso}</span>
                </span>
                <span style={{ color, fontSize:9.5, letterSpacing:"0.08em", textTransform:"uppercase" }}>{c.rail}</span>
                <span style={{ textAlign:"right" }}>{fmtAbbr(v)}</span>
                <span style={{ textAlign:"right", color:"var(--term-ink-2)" }}>{c.count}</span>
                <span style={{ position:"relative", height:4, background:"var(--term-line-dim)" }}>
                  <span style={{ position:"absolute", inset:0, width:`${share * 100}%`, background:color, opacity:0.7 }} />
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
